import { partWeight, buildWeight, weightLabel } from './weight.js';
import { entryPrice, buildPrice } from './composition.js';

export const COMPARE_PARTS = [
  ['framesets', 'Frameset'], ['bars', 'Bars/Stem'], ['groupsets', 'Groupset'], ['wheels', 'Wheels'], ['tyres', 'Tyres'],
  ['seatposts', 'Seatpost'], ['saddles', 'Saddle'], ['cages', 'Bottle cages'], ['pedals', 'Pedals']
];

const MINUS = '−';
const number = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });
export const money = value => `${number.format(Math.round(value * 100) / 100)}\u00a0€`;
export const signedMoney = value => `${value < 0 ? MINUS : '+'}${money(Math.abs(value))}`;
export const signedWeight = grams => `${grams < 0 ? MINUS : '+'}${weightLabel(Math.abs(grams))}`;

function part(entry) {
  if (!entry || !entry.item) return null;
  const choice = entry.item.colors && entry.item.colors[entry.color];
  const colour = !entry.item.noCages && !entry.item.noPedals && choice && choice.color && choice.color.colorName;
  const weight = partWeight(entry);
  return {
    id: entry.item.id, color: entry.color, tyreWidth: entry.tyreWidth || null, name: entry.item.name,
    detail: [colour, entry.tyreWidth && `${entry.tyreWidth} mm`].filter(Boolean).join(' · '),
    price: entryPrice(entry), grams: weight ? weight.grams : null
  };
}

const samePart = (a, b) => !a && !b || !!a && !!b && a.id === b.id && a.color === b.color && a.tyreWidth === b.tyreWidth;

export function deltaLabel(price, grams) {
  return [price ? signedMoney(price) : '', grams ? signedWeight(grams) : ''].filter(Boolean).join(' · ') || 'Same price';
}

export function compareBuilds(left = [], right = []) {
  const find = (selection, id) => part(selection.find(entry => entry.props && entry.props.id === id));
  const rows = COMPARE_PARTS.map(([id, label]) => {
    const a = find(left, id), b = find(right, id);
    const price = (b ? b.price : 0) - (a ? a.price : 0);
    const grams = a && b && a.grams != null && b.grams != null ? b.grams - a.grams : null;
    return { id, label, a, b, same: samePart(a, b), price, grams, delta: deltaLabel(price, grams) };
  }).filter(row => row.a || row.b);
  const total = selection => ({ price: buildPrice(selection), weight: buildWeight(selection) });
  return {
    rows, a: total(left), b: total(right), differences: rows.filter(row => !row.same).length,
    // A differing part without a published weight makes the weight delta partial.
    weightGaps: rows.some(row => !row.same && row.grams == null)
  };
}

// "Build B costs 1.240 € more and is 380 g lighter"; with other: "… more than A and is …"
export function compareSummary(name, { a, b, weightGaps }, other = '') {
  const price = Math.round((b.price - a.price) * 100) / 100;
  const cost = price ? `costs ${money(Math.abs(price))} ${price > 0 ? 'more' : 'less'}${other ? ' than ' + other : ''}`
    : `costs the same${other ? ' as ' + other : ''}`;
  if (!a.weight || !b.weight || weightGaps) return `${name} ${cost}`;
  const grams = Math.round(b.weight.grams - a.weight.grams);
  const weight = grams ? `is ${weightLabel(Math.abs(grams))} ${grams > 0 ? 'heavier' : 'lighter'}` : 'weighs the same';
  return `${name} ${cost} and ${weight}`;
}
